import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

interface WorkflowNavigationOptions {
  clientId: string | number;
  workflowId: string | number;
}

/**
 * Custom hook to handle workflow navigation with automatic version detection
 * 
 * Fetches workflow metadata and determines whether to navigate to v1 or v2 based on:
 * 1. Workflow name starts with '__v2__' (primary method)
 * 2. Created date is after December 1, 2025 (fallback method)
 * 
 * Navigates to:
 * - V2: /workflow2/{clientId}/{workflowId}
 * - V1: /module1/client/{clientId}/{workflowId}
 * 
 * @example
 * const { openWorkflow } = useWorkflowNavigation();
 * 
 * openWorkflow({
 *   clientId: 100115,
 *   workflowId: 1090043
 * });
 */
export const useWorkflowNavigation = () => {
  const navigate = useNavigate();

  const isV2Workflow = useCallback(async (workflowId: string | number): Promise<boolean> => {
    try {
      // Import workflowService dynamically (same pattern as useUpstreamModuleCheck)
      const { workflowService } = await import('../services/api/workflowService');
      
      // Get workflow using the same method as useUpstreamModuleCheck
      const dbWorkflow = await workflowService.getDbWorkflow(workflowId);

      const nameToCheck = dbWorkflow.name;
      if (nameToCheck && nameToCheck.toLowerCase().startsWith('__v2__')) {
        console.log(`[useWorkflowNavigation] Detected v2 workflow by name: "${nameToCheck}"`);
        return true;
      }
  
      if (dbWorkflow.created_at) {
         const createdDate = new Date(dbWorkflow.created_at);
         const cutoffDate = new Date('2025-11-28');
         if (createdDate >= cutoffDate) {
           return true;
         }
       }
       
      return false;
    } catch (error) {
      console.error('[useWorkflowNavigation] Error fetching workflow metadata:', error);
      return false;
    }
  }, []);

  const openWorkflow = useCallback(
    async ({ clientId, workflowId }: WorkflowNavigationOptions) => {
      const isV2 = await isV2Workflow(workflowId);

      if (!isV2) {
        // V1 workflow - navigate to standard V1 path
        const v1Path = `/module1/client/${clientId}/${workflowId}`;
        console.log(`[useWorkflowNavigation] Navigating to v1 workflow: ${v1Path}`);
        navigate(v1Path);
        return;
      }

      // V2 workflow - determine the appropriate step based on latest module status
      try {
        const { workflowService } = await import('../services/api/workflowService');
        const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
        
        // Get modules from workflow response
        const modules = ((dbWorkflow as any).modules || []).map((m: any) => ({
          ...m,
          type: m.type || m.module_type,
          name: m.name || m.module_name
        }));

        console.log(`[useWorkflowNavigation] Found ${modules.length} modules for workflow ${workflowId}`);

        // Step paths mapping (index corresponds to step number)
        const stepPaths = [
          '', // 0: File Upload
          'keyword', // 1: Keyword Analysis
          'semantic', // 2: Semantic Analysis
          'batch_config', // 3: Batch Configuration
          'batch_run', // 4: Batch Processing
          'serp_tool', // 5: SERP Tool
          'data_for_seo', // 6: DataForSEO Analysis
          'explore' // 7: Explore Results
        ];

        // Module type to step index mapping
        const moduleTypeToStepIndex: Record<string, number> = {
          'fileupload_v2': 0,
          'module1_v2': 1,
          'module3_v2': 2,
          'module4-config_v2': 3,
          'module4_v2': 4,
          'serp-analysis_v2': 5,
          'dataforseo-analysis_v2': 6,
        };

        // Find the most recent module by module_id
        let mostRecentModule: any = null;
        let mostRecentModuleId: number | null = null;

        modules.forEach((module: any) => {
          if (module.module_id) {
            if (mostRecentModuleId === null || module.module_id > mostRecentModuleId) {
              mostRecentModuleId = module.module_id;
              mostRecentModule = module;
            }
          }
        });

        let targetStepIndex = 0; // Default to File Upload step

        if (mostRecentModule) {
          const currentStepIndex = moduleTypeToStepIndex[mostRecentModule.type];
          
          console.log(`[useWorkflowNavigation] Latest module: ${mostRecentModule.type} (ID: ${mostRecentModuleId}), Status: ${mostRecentModule.status}, Step Index: ${currentStepIndex}`);

          if (currentStepIndex !== undefined) {
            // If the latest module is completed, go to the next step
            if (mostRecentModule.status === 'completed') {
              targetStepIndex = Math.min(currentStepIndex + 1, stepPaths.length - 1);
              console.log(`[useWorkflowNavigation] Latest module completed, navigating to next step: ${targetStepIndex}`);
            } else {
              // If not completed, go to the current step
              targetStepIndex = currentStepIndex;
              console.log(`[useWorkflowNavigation] Latest module not completed, navigating to current step: ${targetStepIndex}`);
            }
          }
        } else {
          console.log(`[useWorkflowNavigation] No modules found, navigating to File Upload step`);
        }

        // Build the target path
        const basePath = `/workflow2/${clientId}/${workflowId}`;
        const targetPath = stepPaths[targetStepIndex] 
          ? `${basePath}/${stepPaths[targetStepIndex]}` 
          : basePath;

        console.log(`[useWorkflowNavigation] Navigating to v2 workflow step ${targetStepIndex}: ${targetPath}`);
        navigate(targetPath);
      } catch (error) {
        console.error('[useWorkflowNavigation] Error determining workflow step:', error);
        // Fallback to base path on error
        const fallbackPath = `/workflow2/${clientId}/${workflowId}`;
        console.log(`[useWorkflowNavigation] Falling back to base path: ${fallbackPath}`);
        navigate(fallbackPath);
      }
    },
    [navigate, isV2Workflow]
  );
  
  return {
    openWorkflow
  };
};

export default useWorkflowNavigation;

